import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Download, Upload, Loader2, Check, X, LayoutDashboard, ChevronDown, ChevronRight, IndentIncrease, IndentDecrease, FileDown, ChevronsUp, ChevronsDown, Wallet, ShieldCheck, ShieldAlert, ArrowUp, ArrowDown, MoveRight, GripVertical } from "lucide-react";
import { DndContext, PointerSensor, closestCenter, useDndContext, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { formatIDR } from "@/lib/constants";
import ConfirmDialog from "@/components/ConfirmDialog";
import KlasifikasiPengeluaranTab from "@/components/cost-control/KlasifikasiPengeluaranTab";
import PengeluaranProyekTab from "@/components/cost-control/PengeluaranProyekTab";
import WorkingOrderTab from "@/components/monitoring/WorkingOrderTab";
import ActiveProjectDialog from "@/components/ActiveProjectDialog";
import ProfitStatusBadge from "@/components/cost-control/ProfitStatusBadge";
import ProfitDrilldownDialog from "@/components/cost-control/ProfitDrilldownDialog";

/**
 * CostControlPage — tabel Cost Control per proyek (BUKAN popup). 2026-06-20
 * (user request): kolom No, Sub No, Deskripsi Pekerjaan, Volume, Satuan,
 * Harga Satuan, Total Harga Kontrak (auto = volume × harga satuan), Expense,
 * Profit (auto = Total Harga Kontrak − Expense). Baris Overhead Cost selalu
 * paling atas, lalu Item Pekerjaan. Input manual (inline row) atau import
 * Excel (template .xlsx). Profit terintegrasi ke Dashboard Monitoring
 * (widget) & kolom Profit di SummaryProjectListView.
 */

const EMPTY_FORM = { no: "", sub_no: "", description: "", volume: "", satuan: "", harga_satuan: "", expense: "" };

const parseNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return isNaN(v) ? 0 : v;
  let s = String(v).trim().replace(/rp/gi, "").replace(/\s/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if ((s.match(/\./g) || []).length > 1) s = s.replace(/\./g, "");
  const n = Number(s);
  return isNaN(n) ? 0 : n;
};

const fmtVol = (v) => Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

// Kelompokkan Item Pekerjaan ber-hierarki: baris ber-No = judul; baris Sub No
// = item leaf. Judul bisa BERTINGKAT via prefix nomor ("II.A" = sub-judul dari
// "II") — judul yang berisi sub-judul juga bisa di-minimize, dan saat
// minimized menampilkan total rekapitulasi SELURUH turunannya (subK/subE).
const withGroup = (list, collapsed) => {
  const out = [];
  const stack = []; // tumpukan judul aktif: { id, no, entry }
  const anyCollapsed = () => stack.some((l) => collapsed.has(l.id));
  for (const it of list) {
    if (it.no) {
      const no = String(it.no);
      while (stack.length && !no.startsWith(stack[stack.length - 1].no + ".")) stack.pop();
      const parent = stack[stack.length - 1];
      const entry = {
        it, isLeader: true, hasSubs: false, hidden: anyCollapsed(),
        depth: parent ? parent.entry.depth + 1 : 0,
        subK: Number(it.total_harga_kontrak || 0), subE: Number(it.expense || 0),
      };
      if (parent) {
        parent.entry.hasSubs = true;
        // nilai judul anak ikut terangkum ke seluruh judul di atasnya
        for (const l of stack) { l.entry.subK += entry.subK; l.entry.subE += entry.subE; }
      }
      out.push(entry);
      stack.push({ id: it.id, no, entry });
    } else {
      const parent = stack[stack.length - 1];
      const k = Number(it.total_harga_kontrak || 0);
      const e = Number(it.expense || 0);
      if (parent) {
        parent.entry.hasSubs = true;
        for (const l of stack) { l.entry.subK += k; l.entry.subE += e; }
      }
      out.push({ it, isLeader: false, hasSubs: false, hidden: anyCollapsed(), depth: parent ? parent.entry.depth + 1 : 0 });
    }
  }
  return out;
};

export default function CostControlPage() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const canEdit = canPerm(user, "projects", "update");
  // Izin khusus Cost Control: Import Excel + tambah/edit/hapus/pindah baris.
  const canEditCC = canPerm(user, "projects", "edit_cost_control");
  const canCreate = canPerm(user, "projects", "create");

  const [proj, setProj] = useState(null);
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const [adding, setAdding] = useState(null); // "overhead" | "pekerjaan" | null
  const [tab, setTab] = useState(searchParams.get("tab") || "rincian"); // "rincian" | "klasifikasi" | "pengeluaran"
  const [mon, setMon] = useState(null); // data monitoring (daftar WO) utk project maintenance
  const [woSummaries, setWoSummaries] = useState({}); // ringkasan mini kontrak/expense/profit per WO
  const [editId, setEditId] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [selIds, setSelIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());
  const fileRef = useRef(null);

  const isMaintenance = !!proj?.is_outlet;
  const woList = mon?.wo_list || [];

  useEffect(() => {
    try { setCollapsed(new Set(JSON.parse(localStorage.getItem(`cc-collapsed-${pid}`) || "[]"))); }
    catch { setCollapsed(new Set()); }
  }, [pid]);
  useEffect(() => {
    try { localStorage.setItem(`cc-collapsed-${pid}`, JSON.stringify([...collapsed])); } catch { /* abaikan */ }
  }, [collapsed, pid]);

  const load = async () => {
    setErr(null);
    try {
      const [p, cc] = await Promise.all([
        api.get(`/active-projects`).then((r) => {
          const all = r.data || [];
          const me = all.find((x) => x.id === pid);
          if (!me) return me;
          // Konteks kanonis: root proyek via rantai parent_project_id (ID stabil).
          let root = me; const seen = new Set([me.id]);
          while (root.parent_project_id && !seen.has(root.parent_project_id)) {
            seen.add(root.parent_project_id);
            root = all.find((x) => x.id === root.parent_project_id) || { id: root.parent_project_id };
          }
          const st = !me.parent_project_id ? "project" : me.project_type === "variation_order" ? "variation_order" : "work_order";
          return { ...me, __scope: { scope_type: st, project_id: root.id, root_name: root.project_name,
            work_order_id: st === "work_order" ? me.id : undefined, variation_order_id: st === "variation_order" ? me.id : undefined,
            label: me.wo_number || me.variation_order_name || me.project_name } };
        }),
        api.get(`/active-projects/${pid}/cost-control`).then((r) => r.data?.items || []),
      ]);
      if (!p) { setErr("Project tidak ditemukan atau Anda tidak memiliki akses."); return; }
      setProj(p);
      setItems(cc);
      setSelIds(new Set());
      loadCompleteness();
      // Project maintenance (is_outlet): muat daftar WO + ringkasan mini per WO.
      if (p.is_outlet) {
        try { setMon(await api.get(`/active-projects/${pid}/monitoring`).then((r) => r.data)); }
        catch { setMon({ wo_list: [] }); }
        try { setWoSummaries(await api.get(`/active-projects/${pid}/cost-control/children-summary`).then((r) => r.data?.summaries || {})); }
        catch { setWoSummaries({}); }
      } else {
        setMon(null);
        setWoSummaries({});
      }
      // R227: profit view kanonis (engine) — satu sumber angka untuk kartu
      // ringkasan + drill-down kelengkapan biaya.
      try { setProfitView((await api.get(`/active-projects/${pid}/cost-control/summary`)).data?.profit_view || null); }
      catch { setProfitView(null); }
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail) || "Gagal memuat Cost Control.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const overhead = useMemo(() => (items || []).filter((i) => i.section === "overhead"), [items]);
  const pekerjaan = useMemo(() => (items || []).filter((i) => i.section !== "overhead"), [items]);
  const totalKontrak = (items || []).reduce((a, i) => a + Number(i.total_harga_kontrak || 0), 0);
  const totalExpense = (items || []).reduce((a, i) => a + Number(i.expense || 0), 0);
  const totalProfit = totalKontrak - totalExpense;
  const marginPct = totalKontrak > 0 ? (totalProfit / totalKontrak) * 100 : null;

  // 2026-10-13 (R168, user request): status kelengkapan biaya — "Selisih"
  // BUKAN laba final sampai biaya dikonfirmasi lengkap oleh pihak berwenang.
  const CC_STATUS_META = {
    belum_ditinjau: { label: "Belum Ditinjau", cls: "text-slate-600 bg-slate-100 border-slate-300" },
    belum_lengkap: { label: "Biaya Belum Lengkap", cls: "text-amber-800 bg-amber-100 border-amber-300" },
    lengkap: { label: "Biaya Lengkap", cls: "text-emerald-800 bg-emerald-100 border-emerald-300" },
    perlu_ditinjau_ulang: { label: "Perlu Ditinjau Ulang", cls: "text-rose-800 bg-rose-100 border-rose-300" },
  };
  const [completeness, setCompleteness] = useState(null);
  const [profitView, setProfitView] = useState(null);  // R227: engine kanonis
  const [ccDrillOpen, setCcDrillOpen] = useState(false);
  const [ccPeriod, setCcPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const loadCompleteness = async () => {
    try { setCompleteness((await api.get(`/active-projects/${pid}/cost-control/completeness`)).data); }
    catch { setCompleteness(null); }
  };
  const confirmCompleteness = async (next) => {
    const note = window.prompt(`Konfirmasi status biaya "${next === "lengkap" ? "Lengkap" : "Belum Lengkap"}" untuk periode ${ccPeriod}.\nCatatan (dasar pemeriksaan, mis. mutasi bank & petty cash periode tsb):`, "");
    if (note === null) return;
    try {
      await api.post(`/active-projects/${pid}/cost-control/completeness`, { period: ccPeriod, status: next, note });
      toast.success("Status kelengkapan biaya tercatat (audit aktif).");
      loadCompleteness();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const payloadOf = (section, f) => {
    const p = {
      section,
      no: f.no, sub_no: f.sub_no, description: f.description,
      volume: parseNum(f.volume), satuan: f.satuan,
      harga_satuan: parseNum(f.harga_satuan),
    };
    // Expense item pekerjaan otomatis dari Klasifikasi Pengeluaran — hanya overhead manual.
    if (section === "overhead") p.expense = parseNum(f.expense);
    return p;
  };

  const saveNew = async (section, f, reset) => {
    try {
      await api.post(`/active-projects/${pid}/cost-control`, payloadOf(section, f));
      toast.success("Baris ditambahkan");
      reset(); setAdding(null);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal menyimpan baris."); }
  };

  const saveEdit = async (item, f, done) => {
    try {
      await api.patch(`/active-projects/${pid}/cost-control/${item.id}`, payloadOf(item.section, f));
      toast.success("Baris diperbarui");
      done(); setEditId(null);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal memperbarui baris."); }
  };

  const doDelete = async () => {
    if (!delItem) return;
    setDelBusy(true);
    try {
      await api.delete(`/active-projects/${pid}/cost-control/${delItem.id}`);
      toast.success("Baris dihapus");
      setDelItem(null);
      await load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menghapus baris.");
    } finally { setDelBusy(false); }
  };

  const toggleSel = (id) => setSelIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAllPekerjaan = (list) => setSelIds((prev) =>
    list.every((i) => prev.has(i.id)) ? new Set() : new Set(list.map((i) => i.id))
  );

  const doBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const res = await api.post(`/active-projects/${pid}/cost-control/bulk-delete`, { ids: [...selIds] });
      toast.success(`${res.data?.deleted ?? selIds.size} baris dihapus`);
      setBulkConfirm(false);
      await load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menghapus baris terpilih.");
    } finally { setBulkBusy(false); }
  };

  const toggleCollapse = (id) => setCollapsed((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // 2026-06-21 (user request): drag-and-drop antar-baris (dnd-kit). Baris JUDUL
  // berpindah sebagai SATU BLOK beserta seluruh sub di bawahnya; baris sub/polos
  // pindah sendiri. Urutan final disimpan via endpoint reorder (optimistik).
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // "Bayangan blok" (2026-06-21, user request): saat baris JUDUL di-drag,
  // seluruh sub di bawahnya ikut tersorot & ikut bergerak — memperjelas
  // bahwa sub ikut berpindah sebagai satu blok.
  const [dragId, setDragId] = useState(null);
  const dragBlockIds = useMemo(() => {
    if (!dragId) return new Set();
    const it = (items || []).find((i) => i.id === dragId);
    if (!it || !(it.no || "").trim()) return new Set();
    const list = it.section === "overhead" ? overhead : pekerjaan;
    const idx = list.findIndex((i) => i.id === dragId);
    const block = new Set([dragId]);
    for (let k = idx + 1; k < list.length; k++) {
      if ((list[k].no || "").trim()) break;
      block.add(list[k].id);
    }
    return block;
  }, [dragId, items, overhead, pekerjaan]);

  const onDragStart = (e) => {
    setDragId(e.active?.id || null);
    // Expand semua grup yang ter-minimize supaya posisi drop terlihat penuh.
    setCollapsed((prev) => (prev.size ? new Set() : prev));
  };

  const onDragEnd = async (e) => {
    setDragId(null);
    const { active, over } = e;
    const byId = Object.fromEntries((items || []).map((i) => [i.id, i]));
    const aIt = byId[active.id];
    if (!aIt) return;
    const aSec = aIt.section === "overhead" ? "overhead" : "pekerjaan";
    const list = aSec === "overhead" ? overhead : pekerjaan;
    const ids = list.map((i) => i.id);
    const aIdx = ids.indexOf(active.id);
    if (aIdx < 0) return;
    let newIds;
    if ((aIt.no || "").trim()) {
      // Blok judul: judul + baris tanpa No sesudahnya sampai judul berikutnya.
      // Target dihitung dari POSISI POINTER — sub blok ikut bergerak bersama
      // judul ("bayangan blok") sehingga bisa keliru menjadi 'over' dnd-kit.
      const block = [active.id];
      for (let k = aIdx + 1; k < list.length; k++) {
        if ((list[k].no || "").trim()) break;
        block.push(list[k].id);
      }
      const rect = active.rect.current.translated;
      if (!rect) return;
      const midY = rect.top + rect.height / 2;
      const rest = ids.filter((id) => !block.includes(id));
      let insertAt = rest.length;
      for (let i = 0; i < rest.length; i++) {
        const el = document.querySelector(`[data-testid="cc-row-${rest[i]}"]`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (midY < (r.top + r.bottom) / 2) { insertAt = i; break; }
      }
      rest.splice(insertAt, 0, ...block);
      if (rest.join("|") === ids.join("|")) return;
      newIds = rest;
    } else {
      if (!over || active.id === over.id) return;
      const oIt = byId[over.id];
      if (!oIt) return;
      const oSec = oIt.section === "overhead" ? "overhead" : "pekerjaan";
      if (aSec !== oSec) return; // lintas section tidak diizinkan
      const oIdx = ids.indexOf(over.id);
      if (oIdx < 0) return;
      newIds = arrayMove(ids, aIdx, oIdx);
    }
    // Optimistik di UI, lalu simpan ke server; gagal → muat ulang.
    const pos = new Map(newIds.map((id, i) => [id, i]));
    const reordered = [...list].sort((a, b) => pos.get(a.id) - pos.get(b.id));
    setItems((prev) => {
      const others = (prev || []).filter((i) => !pos.has(i.id));
      return aSec === "overhead" ? [...reordered, ...others] : [...others, ...reordered];
    });
    try {
      await api.post(`/active-projects/${pid}/cost-control/reorder`, { section: aSec, ordered_ids: newIds });
    } catch (err2) {
      toast.error(formatDetail(err2.response?.data?.detail) || "Gagal menyimpan urutan baris.");
      await load();
    }
  };

  // 2026-10-14 (user request): pindah urutan baris (naikkan/turunkan satu posisi
  // atau pindah ke bawah judul lain) — memanggil endpoint move.
  const moveItem = async (item, body) => {
    try {
      const r = await api.post(`/active-projects/${pid}/cost-control/${item.id}/move`, body);
      if (!r.data?.no_change) await load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal memindahkan baris.");
    }
  };

  // Jadikan judul ↔ sub judul: pindahkan nilai No ↔ Sub No (aturan eksklusif).
  const convertRow = async (item) => {
    const toSub = !!item.no;
    try {
      await api.patch(`/active-projects/${pid}/cost-control/${item.id}`,
        toSub ? { no: "", sub_no: item.no } : { no: item.sub_no, sub_no: "" });
      toast.success(toSub ? "Judul dijadikan sub dari judul di atasnya" : "Sub judul dijadikan judul");
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal mengubah level baris."); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/active-projects/${pid}/cost-control/template.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "template_cost_control.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh template."); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/active-projects/${pid}/cost-control/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `cost_control_${(proj?.project_name || pid).replace(/[^a-z0-9]+/gi, "_")}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Data Cost Control berhasil diekspor");
    } catch { toast.error("Gagal mengekspor data."); }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/active-projects/${pid}/cost-control/import`, fd);
      const d = res.data || {};
      toast.success(`Import selesai: ${d.added || 0} baris ditambahkan${d.skipped ? `, ${d.skipped} dilewati` : ""}${(d.errors || []).length ? `, ${d.errors.length} error` : ""}`);
      (d.errors || []).slice(0, 3).forEach((m) => toast.error(m));
      await load();
    } catch (e2) {
      toast.error(formatDetail(e2.response?.data?.detail) || "Gagal import — gunakan template yang disediakan.");
    } finally { setImportBusy(false); }
  };

  if (err) return (
    <div className="p-6 max-w-2xl" data-testid="cc-error">
      <p className="overline text-slate-500">Cost Control</p>
      <h1 className="font-display text-2xl text-[#0B1D3A] mt-1">{err}</h1>
      <button type="button" data-testid="cc-back-err" onClick={() => nav("/projects")}
        className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold">
        <ArrowLeft size={13} /> Kembali ke Project
      </button>
    </div>
  );
  if (!proj || items === null) return <div className="p-8 text-sm text-slate-500" data-testid="cc-loading"><Loader2 className="inline animate-spin mr-2" size={14} /> Memuat Cost Control…</div>;

  const sectionOf = (key, label, list) => {
    const subK = list.reduce((a, i) => a + Number(i.total_harga_kontrak || 0), 0);
    const subE = list.reduce((a, i) => a + Number(i.expense || 0), 0);
    const subP = subK - subE;
    const rows = withGroup(list, collapsed);
    // 2026-10-14 (user request): list leader (judul bernomor) utk popover "Pindah ke grup".
    const leaders = list.filter((i) => (i.no || "").trim()).map((i) => ({ id: i.id, label: `${i.no} — ${i.description || "-"}` }));
    // Minimize/Expand semua judul sekaligus (2026-06-20, user request).
    const leaderIds = rows.filter((r) => r.isLeader && r.hasSubs).map((r) => r.it.id);
    const allCollapsed = leaderIds.length > 0 && leaderIds.every((id) => collapsed.has(id));
    const toggleAllCollapse = () => setCollapsed(allCollapsed ? new Set() : new Set(leaderIds));
    // 2026-06-21 (user request): id baris yang bisa di-drag — baris yang
    // tersembunyi (grup ter-minimize) atau sedang diedit tidak ikut.
    const sortableIds = rows.filter((r) => !r.hidden && r.it.id !== editId).map((r) => r.it.id);
    return [
      <SortableContext key={`dnd-${key}`} items={sortableIds} strategy={verticalListSortingStrategy}>{[
      <tr key={`h-${key}`} className="bg-[#0B1D3A]/5 border-y border-slate-300" data-testid={`cc-section-${key}`}>
        <td colSpan={11} className="px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#0B1D3A]">{label} <span className="text-slate-400 font-normal">({list.length} baris)</span></span>
              {key === "pekerjaan" && canEditCC && list.length > 0 && (
                <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 cursor-pointer">
                  <input type="checkbox" className="accent-[#0B1D3A]" data-testid="cc-select-all-pekerjaan"
                    checked={list.length > 0 && list.every((i) => selIds.has(i.id))}
                    onChange={() => toggleAllPekerjaan(list)} />
                  Pilih Semua
                </label>
              )}
            </span>
            <div className="flex items-center gap-2">
              {key === "pekerjaan" && leaderIds.length > 0 && (
                <button type="button" onClick={toggleAllCollapse}
                  data-testid="cc-toggle-all"
                  className="inline-flex items-center gap-1 px-2 py-1 border border-slate-400 text-slate-600 hover:bg-slate-200 text-[10px] uppercase tracking-wider font-semibold">
                  {allCollapsed ? <ChevronsDown size={11} /> : <ChevronsUp size={11} />}
                  {allCollapsed ? "Expand Semua" : "Minimize Semua"}
                </button>
              )}
              {key === "pekerjaan" && canEditCC && selIds.size > 0 && (
                <button type="button" onClick={() => setBulkConfirm(true)}
                  data-testid="cc-bulk-del-btn"
                  className="inline-flex items-center gap-1 px-2 py-1 border border-rose-600 text-rose-700 hover:bg-rose-600 hover:text-white text-[10px] uppercase tracking-wider font-semibold">
                  <Trash2 size={11} /> Hapus Terpilih ({selIds.size})
                </button>
              )}
              {canEditCC && (
                <button type="button" onClick={() => { setAdding(key); setEditId(null); }} disabled={adding === key}
                  data-testid={`cc-add-${key}`}
                  className="inline-flex items-center gap-1 px-2 py-1 border border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-40">
                  <Plus size={11} /> Tambah Baris
                </button>
              )}
            </div>
          </div>
        </td>
      </tr>,
      ...(adding === key ? [
        <EditRow key={`add-${key}`} initial={EMPTY_FORM} testidPrefix={`cc-new-${key}`}
          expenseLocked={key === "pekerjaan"} expenseBase={0}
          onSave={(f, reset) => saveNew(key, f, reset)} onCancel={() => setAdding(null)} />,
      ] : []),
      ...rows.filter((r) => !r.hidden).map(({ it: item, isLeader, hasSubs, subK, subE, depth }) => (
        editId === item.id
          ? <EditRow key={item.id} initial={{
              no: item.no || "", sub_no: item.sub_no || "", description: item.description || "",
              volume: item.volume || "", satuan: item.satuan || "",
              harga_satuan: item.harga_satuan || "", expense: item.expense || "",
            }} testidPrefix={`cc-edit-${item.id}`}
              expenseLocked={item.section === "pekerjaan"} expenseBase={item.expense || 0}
              onSave={(f, reset) => saveEdit(item, f, reset)} onCancel={() => setEditId(null)} />
          : <ItemRow key={item.id} item={item} canEdit={canEditCC}
              inDragBlock={dragBlockIds.has(item.id)}
              selectable={key === "pekerjaan" && canEditCC}
              selected={selIds.has(item.id)}
              onToggleSel={() => toggleSel(item.id)}
              isLeader={isLeader} hasSubs={hasSubs} subK={subK} subE={subE} depth={depth || 0}
              isCollapsed={isLeader && collapsed.has(item.id)}
              onToggleCollapse={() => toggleCollapse(item.id)}
              onConvert={key === "pekerjaan" ? convertRow : undefined}
              leaders={leaders}
              onMove={canEditCC ? (dir) => moveItem(item, { direction: dir }) : undefined}
              onMoveToLeader={canEditCC ? (leaderId) => moveItem(item, { target_leader_id: leaderId }) : undefined}
              onEdit={() => { setEditId(item.id); setAdding(null); }} onDelete={() => setDelItem(item)} />
      )),
      list.length === 0 && adding !== key && (
        <tr key={`empty-${key}`}><td colSpan={11} className="px-3 py-3 text-center text-[11px] text-slate-400 italic" data-testid={`cc-empty-${key}`}>Belum ada baris — klik "Tambah Baris" atau import dari Excel.</td></tr>
      ),
      <tr key={`sub-${key}`} className="bg-slate-50 border-b border-slate-200" data-testid={`cc-subtotal-${key}`}>
        <td colSpan={7} className="px-3 py-1.5 text-right text-[10px] uppercase tracking-wider font-bold text-slate-500">Subtotal {label}</td>
        <td className="px-2 py-1.5 text-right tabular-nums font-bold text-[#0B1D3A]">{formatIDR(subK)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums font-bold text-rose-700">{formatIDR(subE)}</td>
        <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${subP >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid={`cc-subtotal-profit-${key}`}>{formatIDR(subP)}</td>
        <td></td>
      </tr>,
    ]}</SortableContext>,
    ];
  };

  return (
    <div className="p-6 space-y-4" data-testid="cost-control-page">
      {/* Breadcrumb */}
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5">
        <Link to="/projects" className="hover:text-[#0B1D3A]">Proyek</Link>
        <span className="text-slate-400">›</span>
        <Link to="/projects" className="hover:text-[#0B1D3A]">Manajemen Proyek</Link>
        <span className="text-slate-400">›</span>
        <span className="text-slate-700">Cost Control</span>
      </div>

      {/* Header + toolbar */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0B1D3A] tracking-tight" data-testid="cc-title">Cost Control – {proj.__scope?.root_name || proj.project_name}</h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-[#0B1D3A] text-[#0B1D3A] bg-white" data-testid="cc-scope-badge">
              Cakupan: {proj.__scope?.scope_type === "work_order" ? `WO ${proj.__scope.label}` : proj.__scope?.scope_type === "variation_order" ? `VO ${proj.__scope.label}` : "Seluruh Proyek"}
            </span>
            <span className="text-[11px] text-slate-500" data-testid="cc-scope-note">
              {proj.__scope?.scope_type === "work_order" ? "Rincian Cost Control khusus WO ini; Klasifikasi Pengeluaran menampilkan seluruh pengeluaran proyek dengan WO ini sebagai konteks alokasi."
                : proj.__scope?.scope_type === "variation_order" ? "Rincian Cost Control khusus VO ini; Klasifikasi Pengeluaran menampilkan seluruh pengeluaran proyek dengan VO ini sebagai konteks alokasi."
                  : "Menampilkan seluruh pengeluaran proyek, termasuk VO/WO di bawahnya."}
            </span>
          </div>
          <div className="text-xs text-slate-600 mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-[#0B1D3A]">{proj.client || "—"}</span>
            <span className="text-slate-400">•</span>
            <span>{proj.contract_number || proj.po_number || proj.wo_number || "—"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {/* 2026-06-20 (user request): tombol kembali ke Dashboard Monitoring —
              pasangan dari tombol "Buka Cost Control" di donut Profit. */}
          <button type="button" data-testid="cc-btn-dashboard" onClick={() => nav(`/projects/${pid}/monitoring`)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-xs uppercase tracking-wider font-semibold">
            <LayoutDashboard size={13} /> Dashboard Monitoring
          </button>
          <button type="button" data-testid="cc-btn-back" onClick={() => nav("/projects")}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-700">
            <ArrowLeft size={13} /> Kembali ke Project
          </button>
          {canEditCC && (
            <>
              <button type="button" data-testid="cc-btn-import" onClick={() => fileRef.current?.click()} disabled={importBusy}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50">
                {importBusy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {importBusy ? "Mengimpor…" : "Import Excel"}
              </button>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onImportFile} data-testid="cc-import-file" />
            </>
          )}
          {/* Template download tetap tersedia untuk user read-only (hanya melihat format). */}
          <button type="button" data-testid="cc-btn-template" onClick={downloadTemplate}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-700">
            <Download size={13} /> Template Excel
          </button>
          <button type="button" data-testid="cc-btn-export" onClick={exportExcel}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-700">
            <FileDown size={13} /> Export Excel
          </button>
        </div>
      </div>
      {/* Tab: Rincian (tabel item) vs Klasifikasi Pengeluaran (alokasi pembayaran → expense) */}
      <div className="flex gap-1 border-b border-slate-200" data-testid="cc-tabs">
        {[["rincian", "Rincian Cost Control"], ["klasifikasi", "Klasifikasi Pengeluaran"], ["pengeluaran", "Pengeluaran Proyek"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => { setTab(k); if (k === "rincian") load(); }} data-testid={`cc-tab-${k}`}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-[#0B1D3A] text-[#0B1D3A]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "pengeluaran" ? (
        isMaintenance
          ? <WoListForCostControl mon={mon} project={proj} mode="pengeluaran" summaries={woSummaries} canCreate={canCreate} onChanged={load} />
          : <PengeluaranProyekTab pid={pid} />
      ) : tab === "klasifikasi" ? (
        <KlasifikasiPengeluaranTab pid={pid} canEdit={canEdit} wos={woList} scope={proj?.__scope} />
      ) : isMaintenance ? (
        <WoListForCostControl mon={mon} project={proj} mode="rincian" summaries={woSummaries} canCreate={canCreate} onChanged={load} />
      ) : (
      <>
      <p className="text-[11px] text-slate-500 -mt-2" data-testid="cc-import-hint">
        Import Excel khusus <span className="font-semibold text-slate-700">Item Pekerjaan</span> — Overhead Cost tetap diinput manual via tombol "Tambah Baris".
        Kolom <span className="font-semibold text-slate-700">Total Harga Kontrak</span> divalidasi otomatis (harus = Volume × Harga Satuan); baris yang salah ditolak & tidak terunggah.
      </p>

      {/* Status kelengkapan biaya — gerbang interpretasi "Selisih" */}
      {completeness && (
        <div className="border border-slate-200 bg-white p-3 flex items-center gap-3 flex-wrap" data-testid="cc-completeness">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Kelengkapan biaya periode</span>
          <input type="month" value={ccPeriod} onChange={(e) => setCcPeriod(e.target.value)} data-testid="cc-period"
            className="h-7 text-xs border border-slate-300 px-2" />
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border ${CC_STATUS_META[completeness.status]?.cls}`} data-testid="cc-completeness-badge">
            {CC_STATUS_META[completeness.status]?.label || completeness.status}
          </span>
          {completeness.confirmation && (
            <span className="text-[10px] text-slate-500" data-testid="cc-completeness-meta">
              {completeness.confirmation.confirmed_by_name} · {new Date(completeness.confirmation.confirmed_at).toLocaleString("id-ID")}
              {completeness.confirmation.note ? ` · "${completeness.confirmation.note}"` : ""}
            </span>
          )}
          {completeness.status === "perlu_ditinjau_ulang" && (
            <span className="text-[10px] text-rose-700 italic" data-testid="cc-completeness-changed">Data biaya berubah setelah konfirmasi terakhir — konfirmasi ulang diperlukan (histori tersimpan).</span>
          )}
          {completeness.status !== "lengkap" && (
            <span className="text-[10px] text-amber-700 italic" data-testid="cc-completeness-hint">Angka "Selisih" di bawah adalah indikator sementara, bukan laba final.</span>
          )}
          {canEdit && (
            <span className="ml-auto flex items-center gap-1.5">
              <button type="button" onClick={() => confirmCompleteness("belum_lengkap")} data-testid="cc-mark-incomplete"
                className="h-7 px-2.5 border border-amber-300 text-amber-800 text-[10px] uppercase tracking-wider font-semibold hover:bg-amber-50 flex items-center gap-1"><ShieldAlert size={10} /> Belum Lengkap</button>
              <button type="button" onClick={() => confirmCompleteness("lengkap")} data-testid="cc-confirm-complete"
                className="h-7 px-2.5 border border-emerald-300 text-emerald-800 text-[10px] uppercase tracking-wider font-semibold hover:bg-emerald-50 flex items-center gap-1"><ShieldCheck size={10} /> Konfirmasi Lengkap</button>
            </span>
          )}
        </div>
      )}

      {/* Kartu ringkasan */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="cc-summary-cards">
        <div className="border border-slate-200 bg-white p-3" data-testid="cc-card-kontrak">
          <div className="overline text-slate-500">Total Harga Kontrak</div>
          <div className="text-lg font-bold tabular-nums text-[#0B1D3A] mt-1">{formatIDR(totalKontrak)}</div>
        </div>
        <div className="border border-slate-200 bg-white p-3" data-testid="cc-card-expense">
          <div className="overline text-slate-500">Total Biaya Tercatat</div>
          <div className="text-lg font-bold tabular-nums text-rose-700 mt-1">{formatIDR(totalExpense)}</div>
        </div>
        <div className="border border-slate-200 bg-white p-3" data-testid="cc-card-profit" title="Dasar Pendapatan − Biaya Diakui. Label mengikuti status kelengkapan biaya (engine kanonis).">
          {/* R227: label & warna dari profit_view kanonis — hijau HANYA saat
              Biaya Lengkap & positif; selain itu "Selisih Sementara". */}
          <div className="overline text-slate-500 flex items-center gap-1.5 flex-wrap">
            {profitView?.labels?.selisih || "Selisih Sementara"} (Kontrak − Biaya)
            {profitView && <ProfitStatusBadge view={profitView} small testid="cc-card-badge" onClick={() => setCcDrillOpen(true)} />}
          </div>
          <button type="button" data-testid="cc-card-profit-btn" onClick={() => setCcDrillOpen(true)}
            className={`text-lg font-bold tabular-nums mt-1 text-left hover:underline underline-offset-2 ${profitView?.completeness?.status === "complete" ? (totalProfit >= 0 ? "text-emerald-700" : "text-rose-700") : profitView?.completeness?.status === "incomplete" ? "text-amber-700" : profitView ? "text-slate-500 italic text-sm" : totalProfit >= 0 ? "text-emerald-700" : "text-rose-700"}`}
            title="Klik untuk rincian pembentuk angka">
            {/* R229: status unknown → TANPA angka selisih bertanda di kartu. */}
            {profitView?.completeness?.status === "unknown" ? "Belum Dapat Dipastikan" : formatIDR(profitView?.selisih ?? totalProfit)}
          </button>
          {(profitView?.pending?.total ?? 0) > 0 && profitView?.completeness?.status !== "unknown" && (
            <div className="text-[10px] text-amber-700 tabular-nums" data-testid="cc-card-pending">+ biaya tertunda {formatIDR(profitView.pending.total)}</div>
          )}
        </div>
        <div className="border border-slate-200 bg-white p-3" data-testid="cc-card-margin" title="Selisih ÷ Dasar Pendapatan (Total Harga Kontrak CC). Dasar kosong/0 → belum dapat dihitung.">
          <div className="overline text-slate-500">{profitView?.labels?.margin || "Margin Sementara"}</div>
          <button type="button" data-testid="cc-card-margin-btn" onClick={() => setCcDrillOpen(true)}
            className="text-lg font-bold tabular-nums text-slate-700 mt-1 text-left hover:underline underline-offset-2">
            {(profitView ? profitView.margin : marginPct) === null || profitView?.completeness?.status === "unknown" ? "Belum dapat dihitung" : `${(profitView ? profitView.margin : marginPct).toFixed(1)}%`}
          </button>
          {profitView && profitView.margin !== null && profitView.completeness?.status === "incomplete" && (
            <div className="text-[9px] text-slate-500">sementara — menunggu kelengkapan biaya</div>
          )}
        </div>
      </div>
      <ProfitDrilldownDialog open={ccDrillOpen} onOpenChange={setCcDrillOpen} view={profitView} projectName={proj?.project_name} />

      {/* Tabel cost control — Overhead paling atas, lalu Item Pekerjaan */}
      <div className="border border-slate-200 overflow-auto max-h-[70vh] bg-white" data-testid="cc-table-wrap">
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragId(null)}>
        <table className="w-full text-xs min-w-[980px]">
          <thead>
            <tr className="text-left">
              <th className="pl-2 py-2 w-8 sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300" data-testid="cc-th-select"></th>
              <th className="px-2 py-2 overline text-slate-700 w-14 sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300">No</th>
              <th className="px-2 py-2 overline text-slate-700 w-14 sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300">Sub No</th>
              <th className="px-2 py-2 overline text-slate-700 sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300">Deskripsi Pekerjaan</th>
              <th className="px-2 py-2 overline text-slate-700 w-20 text-right sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300">Volume</th>
              <th className="px-2 py-2 overline text-slate-700 w-16 sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300">Satuan</th>
              <th className="px-2 py-2 overline text-slate-700 w-28 text-right sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300">Harga Satuan</th>
              <th className="px-2 py-2 overline text-slate-700 w-28 text-right sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300">Total Harga Kontrak</th>
              <th className="px-2 py-2 overline text-slate-700 w-28 text-right sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300">Expense</th>
              <th className="px-2 py-2 overline text-slate-700 w-28 text-right sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300">Selisih</th>
              <th className="px-2 py-2 overline text-slate-700 w-20 text-right sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-300">Aksi</th>
            </tr>
          </thead>
          <tbody data-testid="cc-tbody">
            {sectionOf("overhead", "Overhead Cost", overhead)}
            {sectionOf("pekerjaan", "Item Pekerjaan", pekerjaan)}
            <tr className="bg-[#0B1D3A] text-white" data-testid="cc-grand-total">
              <td colSpan={7} className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-bold">Grand Total</td>
              <td className="px-2 py-2 text-right tabular-nums font-bold" data-testid="cc-grand-kontrak">{formatIDR(totalKontrak)}</td>
              <td className="px-2 py-2 text-right tabular-nums font-bold" data-testid="cc-grand-expense">{formatIDR(totalExpense)}</td>
              <td className="px-2 py-2 text-right tabular-nums font-bold" data-testid="cc-grand-profit">{formatIDR(totalProfit)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        </DndContext>
      </div>
      </>
      )}

      <ConfirmDialog
        open={bulkConfirm}
        onOpenChange={(v) => { if (!v) setBulkConfirm(false); }}
        title="Hapus Baris Terpilih?"
        description={`${selIds.size} baris item pekerjaan akan dihapus sekaligus. Profit proyek akan dihitung ulang otomatis.`}
        confirmLabel={bulkBusy ? "Menghapus…" : `Hapus ${selIds.size} Baris`}
        onConfirm={doBulkDelete}
        variant="danger"
        testid="cc-confirm-bulk-delete"
      />

      <ConfirmDialog
        open={!!delItem}
        onOpenChange={(v) => { if (!v) setDelItem(null); }}
        title="Hapus Baris Cost Control?"
        description={delItem ? `"${delItem.description}" (${formatIDR(delItem.total_harga_kontrak || 0)}). Profit proyek akan dihitung ulang otomatis.` : ""}
        confirmLabel={delBusy ? "Menghapus…" : "Hapus"}
        onConfirm={doDelete}
        variant="danger"
        testid="cc-confirm-delete"
      />
    </div>
  );
}

function MoveToLeaderMenu({ itemId, leaders, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <span className="relative inline-block" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} title="Pindah ke grup/judul lain"
        data-testid={`cc-move-to-${itemId}`}
        className="p-1.5 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-100"><MoveRight size={13} /></button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-300 shadow-xl z-20 max-h-72 overflow-y-auto" data-testid={`cc-move-menu-${itemId}`}>
          <button type="button" onClick={() => { setOpen(false); onPick(null); }}
            className="block w-full text-left px-3 py-2 text-[11px] hover:bg-slate-50 border-b border-slate-100">
            — Pindah ke awal section —
          </button>
          {leaders.map((l) => (
            <button key={l.id} type="button" onClick={() => { setOpen(false); onPick(l.id); }}
              data-testid={`cc-move-pick-${itemId}-${l.id}`}
              className="block w-full text-left px-3 py-2 text-[11px] hover:bg-slate-50 truncate">
              {l.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function ItemRow({ item, canEdit, onEdit, onDelete, selectable, selected, onToggleSel, isLeader, hasSubs, isCollapsed, onToggleCollapse, onConvert, subK, subE, depth = 0, onMove, onMoveToLeader, leaders = [], inDragBlock = false }) {
  const profit = Number(item.total_harga_kontrak || 0) - Number(item.expense || 0);
  // 2026-06-21 (user request): baris bisa di-drag (handle grip di kolom Aksi).
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !canEdit });
  // "Bayangan blok": sub dari judul yang sedang di-drag ikut bergerak bersama
  // judulnya (mengikuti transform active) + tersorot, bukan bergeser minggir.
  const { active } = useDndContext();
  const isBlockFollower = inDragBlock && active && active.id !== item.id && active.transform;
  const dragStyle = isBlockFollower
    ? { transform: CSS.Translate.toString(active.transform), opacity: 0.45, position: "relative", zIndex: 9 }
    : {
        transform: CSS.Transform.toString(transform), transition,
        opacity: isDragging ? 0.45 : undefined,
        position: isDragging ? "relative" : undefined,
        zIndex: isDragging ? 10 : undefined,
      };
  // Judul grup dgn sub: EXPANDED → tanpa angka (murni baris judul);
  // MINIMIZED → tampilkan total rekapitulasi semua sub di dalamnya.
  const dim = isLeader && hasSubs;
  const showSub = dim && isCollapsed;
  const profitVal = showSub ? subK - subE : Number(item.profit ?? profit);
  const subTip = "Rekapitulasi judul — gabungan semua sub di bawahnya";
  return (
    <tr ref={setNodeRef} style={dragStyle} className={`border-b border-slate-100 hover:bg-slate-50 ${selected ? "bg-rose-50/60" : ""} ${isLeader ? "bg-slate-50/70" : ""} ${isDragging ? "shadow-lg" : ""} ${inDragBlock ? "bg-[#0B1D3A]/10 shadow-[inset_3px_0_0_#0B1D3A]" : ""}`} data-testid={`cc-row-${item.id}`}>
      <td className="pl-2 py-1.5 w-8">
        {selectable && (
          <input type="checkbox" className="accent-[#0B1D3A] cursor-pointer" checked={!!selected} onChange={onToggleSel}
            title="Pilih baris untuk hapus massal" data-testid={`cc-select-${item.id}`} />
        )}
      </td>
      <td className="px-2 py-1.5 text-slate-700">
        {isLeader && hasSubs ? (
          <span className="inline-flex items-center gap-0.5">
            <button type="button" onClick={onToggleCollapse}
              title={isCollapsed ? "Tampilkan sub judul" : "Minimize sub judul"}
              data-testid={`cc-collapse-${item.id}`}
              className="p-0.5 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-200 rounded">
              {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
            </button>
            <span className="font-bold">{item.no}</span>
          </span>
        ) : (item.no || "—")}
      </td>
      <td className="px-2 py-1.5 text-slate-700">{item.sub_no || "—"}</td>
      <td className={`px-2 py-1.5 break-words ${isLeader ? "font-bold text-[#0B1D3A] uppercase tracking-wide" : "text-slate-800"}`} style={{ paddingLeft: `${((isLeader ? depth : depth + 1) * 14) + 8}px` }}>{item.description}</td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{dim ? "—" : fmtVol(item.volume)}</td>
      <td className="px-2 py-1.5 text-slate-700">{item.satuan || "—"}</td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{dim ? "—" : formatIDR(item.harga_satuan || 0)}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums font-semibold text-[#0B1D3A] ${showSub ? "font-bold border-t-2 border-[#0B1D3A]/30" : ""}`} title={showSub ? subTip : undefined} data-testid={`cc-kontrak-${item.id}`}>{showSub ? formatIDR(subK) : dim ? "—" : formatIDR(Number(item.total_harga_kontrak || 0))}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums text-rose-700 ${showSub ? "font-bold border-t-2 border-[#0B1D3A]/30" : ""}`} title={showSub ? subTip : item.section === "pekerjaan" && !isLeader ? "Otomatis dari Klasifikasi Pengeluaran" : undefined}>{showSub ? formatIDR(subE) : dim ? "—" : formatIDR(Number(item.expense || 0))}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${profitVal >= 0 ? "text-emerald-700" : "text-rose-700"} ${showSub ? "border-t-2 border-[#0B1D3A]/30" : ""}`} title={showSub ? subTip : undefined} data-testid={`cc-profit-${item.id}`}>{dim && !showSub ? "—" : formatIDR(profitVal)}</td>
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        {canEdit ? (
          <>
            <button type="button" {...attributes} {...listeners}
              title="Seret untuk memindahkan baris — baris judul ikut beserta seluruh sub di bawahnya"
              data-testid={`cc-drag-${item.id}`}
              className="p-1.5 text-slate-400 hover:text-[#0B1D3A] hover:bg-slate-100 cursor-grab active:cursor-grabbing touch-none">
              <GripVertical size={13} /></button>
            {onMove && (
              <>
                <button type="button" onClick={() => onMove("up")} title="Naikkan baris" data-testid={`cc-move-up-${item.id}`}
                  className="p-1.5 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-100"><ArrowUp size={13} /></button>
                <button type="button" onClick={() => onMove("down")} title="Turunkan baris" data-testid={`cc-move-down-${item.id}`}
                  className="p-1.5 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-100"><ArrowDown size={13} /></button>
              </>
            )}
            {onMoveToLeader && leaders.length > 0 && !isLeader && (
              <MoveToLeaderMenu itemId={item.id} leaders={leaders.filter((l) => l.id !== item.id)} onPick={onMoveToLeader} />
            )}
            {onConvert && (item.no || item.sub_no) && (
              <button type="button" onClick={() => onConvert(item)}
                title={item.no ? "Jadikan sub dari judul di atasnya" : "Jadikan judul"}
                data-testid={`cc-convert-${item.id}`}
                className="p-1.5 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-100">
                {item.no ? <IndentIncrease size={13} /> : <IndentDecrease size={13} />}
              </button>
            )}
            <button type="button" onClick={onEdit} title="Ubah baris" data-testid={`cc-edit-btn-${item.id}`}
              className="p-1.5 text-slate-500 hover:text-[#0B1D3A] hover:bg-slate-100"><Pencil size={13} /></button>
            <button type="button" onClick={onDelete} title="Hapus baris" data-testid={`cc-del-btn-${item.id}`}
              className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50"><Trash2 size={13} /></button>
          </>
        ) : <span className="text-[10px] text-slate-400 italic">—</span>}
      </td>
    </tr>
  );
}

function EditRow({ initial, onSave, onCancel, testidPrefix, expenseLocked, expenseBase }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const total = parseNum(f.volume) * parseNum(f.harga_satuan);
  const profit = total - (expenseLocked ? Number(expenseBase || 0) : parseNum(f.expense));
  const bothFilled = f.no.trim() !== "" && f.sub_no.trim() !== "";
  const inp = "w-full border border-slate-300 px-1.5 py-1 text-xs focus:outline-none focus:border-[#0B1D3A] bg-white";
  const doSave = async () => {
    if (!f.description.trim()) { toast.error("Deskripsi pekerjaan wajib diisi."); return; }
    if (bothFilled) { toast.error("Isi No atau Sub No — tidak bisa keduanya. No = judul, Sub No = sub dari judul."); return; }
    setBusy(true);
    await onSave(f, () => setF(EMPTY_FORM));
    setBusy(false);
  };
  return (
    <tr className="border-b border-amber-200 bg-amber-50/60" data-testid={`${testidPrefix}-row`}>
      <td className="pl-2 py-1.5"></td>
      <td className="px-2 py-1.5"><input value={f.no} onChange={set("no")} title="No = judul grup (Sub No harus kosong)" className={`${inp} ${bothFilled ? "border-rose-500 bg-rose-50" : ""}`} data-testid={`${testidPrefix}-no`} /></td>
      <td className="px-2 py-1.5"><input value={f.sub_no} onChange={set("sub_no")} title="Sub No = sub dari judul (No harus kosong)" className={`${inp} ${bothFilled ? "border-rose-500 bg-rose-50" : ""}`} data-testid={`${testidPrefix}-subno`} /></td>
      <td className="px-2 py-1.5"><input value={f.description} onChange={set("description")} placeholder="Deskripsi pekerjaan *" autoFocus className={inp} data-testid={`${testidPrefix}-desc`} /></td>
      <td className="px-2 py-1.5"><input value={f.volume} onChange={set("volume")} inputMode="decimal" className={`${inp} text-right`} data-testid={`${testidPrefix}-volume`} /></td>
      <td className="px-2 py-1.5"><input value={f.satuan} onChange={set("satuan")} className={inp} data-testid={`${testidPrefix}-satuan`} /></td>
      <td className="px-2 py-1.5"><input value={f.harga_satuan} onChange={set("harga_satuan")} inputMode="numeric" className={`${inp} text-right`} data-testid={`${testidPrefix}-harga`} /></td>
      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{formatIDR(total)}</td>
      <td className="px-2 py-1.5">{expenseLocked ? (
        <span className="text-[10px] text-slate-400 italic" title="Diisi otomatis dari tab Klasifikasi Pengeluaran" data-testid={`${testidPrefix}-expense-auto`}>
          Otomatis{Number(expenseBase || 0) > 0 ? `: ${formatIDR(expenseBase)}` : ""}
        </span>
      ) : (
        <input value={f.expense} onChange={set("expense")} inputMode="numeric" className={`${inp} text-right`} data-testid={`${testidPrefix}-expense`} />
      )}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatIDR(profit)}</td>
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        <button type="button" onClick={doSave} disabled={busy} title="Simpan" data-testid={`${testidPrefix}-save`}
          className="p-1.5 text-emerald-600 hover:bg-emerald-50 disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        </button>
        <button type="button" onClick={onCancel} title="Batal" data-testid={`${testidPrefix}-cancel`}
          className="p-1.5 text-slate-400 hover:bg-slate-100"><X size={14} /></button>
      </td>
    </tr>
  );
}


// Daftar WO bergaya tab "Working Order" (Dashboard Monitoring) + tombol aksi
// cost control per WO (2026-06-20, user request). Item/expense tiap WO
// tersimpan dgn project_id = id WO → halaman cost control WO memakai rute sama.
function WoListForCostControl({ mon, project, mode, summaries = {}, canCreate = false, onChanged }) {
  const nav = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  if (mon === null) {
    return <div className="p-6 text-sm text-slate-500" data-testid="cc-wo-loading"><Loader2 className="inline animate-spin mr-2" size={14} /> Memuat daftar Working Order…</div>;
  }
  return (
    <div data-testid={`cc-wo-list-${mode}`}>
      {/* 2026-09-29 (user request): tambah WO langsung dari daftar WO Cost Control
          — pembuatan WO & pengisian cost control-nya dalam satu alur. */}
      {canCreate && (
        <div className="flex justify-end">
          <button type="button" onClick={() => setCreateOpen(true)}
            data-testid={`cc-wo-new-btn-${mode}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#FF5A1F] hover:bg-[#e04a13] text-white text-xs uppercase tracking-wider font-semibold">
            <Plus size={13} /> New Working Order
          </button>
        </div>
      )}
      <ActiveProjectDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={onChanged}
        presetCategory="maintenance" presetType="working_order" />
      <WorkingOrderTab mon={mon} project={project} rowAction={(w) => {
        const s = summaries[w.id];
        const margin = s && s.kontrak > 0 ? (s.profit / s.kontrak) * 100 : null;
        return (
          <div className="flex flex-col items-end gap-1">
            {s && (
              <div className="text-[9px] tabular-nums text-slate-500 whitespace-nowrap" title="Ringkasan WO: Kontrak / Expense / Margin" data-testid={`wo-cc-summary-${w.id}`}>
                K <b className="text-[#0B1D3A]">{formatIDR(s.kontrak)}</b>
                {" · "}E <b className="text-rose-700">{formatIDR(s.expense)}</b>
                {" · "}M <b className={margin !== null && margin < 0 ? "text-rose-700" : "text-emerald-700"}>{margin !== null ? `${margin.toFixed(1)}%` : "—"}</b>
              </div>
            )}
            <button type="button"
              onClick={(e) => { e.stopPropagation(); nav(`/projects/${w.id}/cost-control${mode === "pengeluaran" ? "?tab=pengeluaran" : ""}`); }}
              title={mode === "pengeluaran" ? "Buka Pengeluaran Proyek WO ini" : "Buka Rincian Cost Control WO ini"}
              data-testid={`wo-cc-${mode}-${w.id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap">
              <Wallet size={11} /> {mode === "pengeluaran" ? "Pengeluaran Proyek" : "Rincian Cost Control"}
            </button>
          </div>
        );
      }} />
    </div>
  );
}
